import React, { useState, useEffect } from 'react'
import { Building2 } from 'lucide-react'
import { cn } from '../../lib/utils'

interface OrgBrandingPreviewProps {
  orgName: string
  primaryColor: string | null | undefined
  logoUrl: string | null | undefined
  logoDarkUrl?: string | null | undefined
  previewLogoFile?: File
  previewLogoDarkFile?: File
}

export const OrgBrandingPreview: React.FC<OrgBrandingPreviewProps> = ({
  orgName,
  primaryColor,
  logoUrl,
  logoDarkUrl,
  previewLogoFile,
  previewLogoDarkFile,
}) => {
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null)
  const [fileDarkPreviewUrl, setFileDarkPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!previewLogoFile) {
      setFilePreviewUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(previewLogoFile)
    setFilePreviewUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [previewLogoFile])

  useEffect(() => {
    if (!previewLogoDarkFile) {
      setFileDarkPreviewUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(previewLogoDarkFile)
    setFileDarkPreviewUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [previewLogoDarkFile])

  const lightLogoUrl = filePreviewUrl ?? logoUrl ?? null
  const darkLogoUrl = fileDarkPreviewUrl ?? logoDarkUrl ?? lightLogoUrl
  const ctaColor = primaryColor || '#1d4ed8'
  const displayName = orgName || 'Organization Name'

  return (
    <div
      aria-label="Branding preview"
      className="rounded-xl border border-border bg-muted/30 overflow-hidden"
    >
      <div className="px-3 py-2 border-b border-border bg-muted/50">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Preview
        </p>
      </div>

      <div className="p-4 flex flex-col gap-4">
        <PreviewPane
          theme="light"
          logoUrl={lightLogoUrl}
          displayName={displayName}
          ctaColor={ctaColor}
        />
        <PreviewPane
          theme="dark"
          logoUrl={darkLogoUrl}
          displayName={displayName}
          ctaColor={ctaColor}
        />
      </div>
    </div>
  )
}

interface PreviewPaneProps {
  theme: 'light' | 'dark'
  logoUrl: string | null
  displayName: string
  ctaColor: string
}

const PreviewPane: React.FC<PreviewPaneProps> = ({
  theme,
  logoUrl,
  displayName,
  ctaColor,
}) => {
  const darkMode = theme === 'dark'
  return (
    <div className={cn(
      'rounded-lg p-3',
      darkMode ? 'bg-slate-900' : 'bg-white',
    )}>
      <p className={cn(
        'text-[10px] font-medium uppercase tracking-wide mb-2',
        darkMode ? 'text-slate-500' : 'text-slate-400',
      )}>
        {darkMode ? 'Dark mode' : 'Light mode'}
      </p>
      <div className="w-full max-w-[260px] mx-auto space-y-3">
        <div className="text-center space-y-2">
          {logoUrl ? (
            <div className={cn(
              'inline-block rounded-lg p-2',
              darkMode ? 'bg-slate-950' : 'bg-slate-900',
            )}>
              <img
                src={logoUrl}
                alt={`${displayName} logo`}
                className="h-10 w-auto max-w-[100px] object-contain"
              />
            </div>
          ) : (
            <div
              aria-label="Logo placeholder"
              className="mx-auto h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"
            >
              <Building2 className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
          )}
          <p className={cn(
            'text-sm font-bold tracking-tight leading-tight',
            darkMode ? 'text-white' : 'text-slate-900',
          )}>
            Sign in to {displayName}
          </p>
        </div>

        <div className={cn(
          'border rounded-xl p-3 space-y-2',
          darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200 shadow-sm',
        )}>
          <div className="space-y-1">
            <p className={cn('text-[10px]', darkMode ? 'text-slate-400' : 'text-slate-500')}>
              Email address
            </p>
            <div className={cn(
              'h-6 w-full rounded border',
              darkMode ? 'border-slate-600 bg-slate-700' : 'border-slate-200 bg-white',
            )} />
          </div>

          <div className="space-y-1">
            <p className={cn('text-[10px]', darkMode ? 'text-slate-400' : 'text-slate-500')}>
              Password
            </p>
            <div className={cn(
              'h-6 w-full rounded border',
              darkMode ? 'border-slate-600 bg-slate-700' : 'border-slate-200 bg-white',
            )} />
          </div>

          <div className="flex justify-end">
            <span
              className="text-[10px] font-medium"
              style={{ color: ctaColor }}
            >
              Forgot your password?
            </span>
          </div>

          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            className="w-full rounded-md px-3 py-1 text-[10px] font-semibold text-white"
            style={{ backgroundColor: ctaColor }}
          >
            Sign in
          </button>
        </div>
      </div>
    </div>
  )
}
